import { deepSignal } from 'deepsignal';
import CustomizeSection from '@components/overlays/shared/customize-section.jsx';
import Description from '@components/overlays/shared/description.jsx';
import NumberInput from '@components/input/number.input.jsx';
import LinkSetup from '@components/overlays/shared/link-setup.jsx';
import { CHARITY } from '@util/const.js';
import { ListDashboard } from '@playlive/overlay-core/dashboards/list';
import { FundraisingDashboard } from '@playlive/overlay-core/dashboards/fundraising';
import Picker from '@components/input/picker.input.jsx';
import useCommon from '@hooks/useCommon.js';
import useDemoProgressBarData from '@hooks/useDemoProgressBarData.js';

const config = deepSignal({
  charityType: CHARITY.TILTIFY,
  minDonation: 5,
  highlight: 5,
  donorMin: 10,
  dashboardType: 'dashboard',
});

export default function DashboardPage() {
  const { setConfigState, overlayInfo, overlayURL } = useCommon({
    config,
    urlBase: config.dashboardType,
    forceTiltify: true,
  });

  const types = [
    { value: 'dashboard', name: 'Original' },
    { value: 'list-dashboard', name: 'List' },
  ];

  const { demoDonations, demoRewards, demoMilestones } = useDemoProgressBarData(
    {
      showDonors: 'yes',
      showRewards: 'yes',
    }
  );

  let dashboardBlock;

  if (config.dashboardType === 'dashboard')
    dashboardBlock = (
      <FundraisingDashboard
        full={false}
        overallGoalAmount={100}
        overallCurrentAmount={50}
        goalAmount={100}
        currentAmount={50}
        rewards={demoRewards}
        milestones={demoMilestones}
        minimumHighlightAmount={config.highlight}
        minimumDonationAmount={config.minDonation}
        donations={demoDonations}
        donorAmount={config.donorMin}
      />
    );
  else
    dashboardBlock = (
      <ListDashboard
        full={false}
        donations={demoDonations}
        polls={[]}
        rewards={demoRewards}
        campaignID={null}
        goalAmount={100}
        currentAmount={50}
        minimumHighlightAmount={config.highlight}
        minimumDonationAmount={config.minDonation ?? 0}
        showMaxDonations={config.donorMin ?? 0}
        overallCurrentAmount={50}
        overallGoalAmount={100}
      />
    );

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col items-center justify-center overflow-x-hidden">
        <div className="example_overlay select-none overflow-x-hidden w-full">
          <div
            className={`rounded-lg m-0 p-0 overflow-hidden bg-preview w-full ${config.dashboardType === 'dashboard' ? ' h-[764px]' : ' h-[465px] '} border-base-300 border`}
          >
            <div className="border-base-300 border-t m-0 p-0">
              {dashboardBlock}
            </div>
          </div>
        </div>

        <Description>
          <p className="pb-4">
            Effortlessly track your fundraising progress during live streams
            with our intuitive dashboard. It displays claimed rewards, poll
            votes, donor comments, and milestones. Customize the number of
            donors shown, monitor total funds raised, and view the percentage
            remaining to reach your goal.
          </p>
        </Description>

        <div className="mt-4 mb-4 w-full px-2">
          <CustomizeSection title="Appearance">
            <Picker
              items={types}
              fieldName="dashboardType"
              onChange={setConfigState}
              currentValue={config.dashboardType}
              labelText="Type"
            />
          </CustomizeSection>
          <CustomizeSection title="Amounts">
            <NumberInput
              value={Number(config.minDonation)}
              labelText="Minimum donation amount to display in donor list"
              name="minDonation"
              onChange={setConfigState}
            />

            <NumberInput
              value={Number(config.highlight)}
              labelText="Minimum donation amount to highlight in donor list"
              name="highlight"
              onChange={setConfigState}
            />
            <NumberInput
              value={Number(config.donorMin)}
              labelText="Amount of donors to display"
              name="donorMin"
              isCurrency={false}
              onChange={setConfigState}
            />
          </CustomizeSection>

          <LinkSetup
            charityType={CHARITY.TILTIFY}
            obsDimensions={overlayInfo.obsDimensions}
            overlayName={overlayInfo.name}
            overlayURL={overlayURL}
          />
        </div>
      </div>
    </div>
  );
}
